/**
 * User Menu component for authenticated user management.
 *
 * Displays user avatar with dropdown menu containing:
 * - User info with role badge
 * - My Profile link to Self-Service page
 * - Placeholder items (Settings, Help)
 * - About dialog with app information
 * - Sign Out action
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  MenuDivider,
  Avatar,
  Button,
  Text,
  Badge,
  Tooltip,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  SignOut24Regular,
  Person24Regular,
  Settings24Regular,
  QuestionCircle24Regular,
  Info24Regular,
} from '@fluentui/react-icons';
import Markdown from 'react-markdown';
import { useAuth } from '../../contexts/AuthContext';
import changelogContent from '../../../../CHANGELOG.md?raw';

const useStyles = makeStyles({
  menuPopover: {
    minWidth: '270px',
  },
  userInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalM,
  },
  badgeContainer: {
    marginTop: tokens.spacingVerticalXS,
  },
});

/**
 * Get role badge configuration based on user's role.
 * Maps role values to display text and badge colors.
 */
function getRoleBadge(user: {
  role: string;
}): { text: string; color: 'success' | 'informative' | 'subtle' | 'warning' } | null {
  switch (user.role) {
    case 'global_admin':
      return { text: 'Global Admin', color: 'success' };
    case 'organization_owner':
      return { text: 'Org Owner', color: 'success' };
    case 'organization_admin':
      return { text: 'Org Admin', color: 'informative' };
    case 'organization_user':
      return { text: 'Org User', color: 'subtle' };
    case 'managed_tenant_user':
      return { text: 'User', color: 'subtle' };
    default:
      return null;
  }
}

/**
 * User Menu component.
 *
 * Displays user avatar and dropdown menu when authenticated.
 * Shows user info with role badge, navigation links, and logout option.
 * Returns null when user is not authenticated.
 */
export function UserMenu(): JSX.Element | null {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const styles = useStyles();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAboutDialogOpen, setIsAboutDialogOpen] = useState(false);

  // Don't render if no user
  if (!user) {
    return null;
  }

  const roleBadge = getRoleBadge(user);

  const handleLogout = async () => {
    setIsMenuOpen(false);
    await logout();
  };

  const handleNavigateToProfile = () => {
    setIsMenuOpen(false);
    navigate('/self-service');
  };

  const handleOpenAboutDialog = () => {
    setIsMenuOpen(false);
    setIsAboutDialogOpen(true);
  };

  return (
    <>
    <Menu open={isMenuOpen} onOpenChange={(_, data) => setIsMenuOpen(data.open)}>
      <MenuTrigger>
        <Button
          appearance="subtle"
          aria-label="User menu"
          style={{ padding: '4px', minWidth: 'auto' }}
        >
          <Avatar name={user.name} size={36} color="colorful" icon={<Person24Regular />} />
        </Button>
      </MenuTrigger>

      <MenuPopover className={styles.menuPopover}>
        <MenuList>
          {/* User Info Header with Role Badge */}
          <MenuItem disabled>
            <div className={styles.userInfo}>
              <Text weight="semibold">{user.name}</Text>
              <Text size={200}>{user.user_principal_name || user.email}</Text>
              {roleBadge && (
                <div className={styles.badgeContainer}>
                  <Badge appearance="filled" color={roleBadge.color} size="small">
                    {roleBadge.text}
                  </Badge>
                </div>
              )}
            </div>
          </MenuItem>

          <MenuDivider />

          {/* My Profile - Links to Self-Service */}
          <MenuItem
            icon={<Person24Regular />}
            onClick={handleNavigateToProfile}
            data-testid="menu-item-my-profile"
          >
            My Profile
          </MenuItem>

          <MenuDivider />

          {/* Placeholder Items - Coming Soon */}
          <Tooltip content="Coming Soon" relationship="label">
            <MenuItem
              icon={<Settings24Regular />}
              disabled
              data-testid="menu-item-settings"
            >
              Settings
            </MenuItem>
          </Tooltip>

          <Tooltip content="Coming Soon" relationship="label">
            <MenuItem
              icon={<QuestionCircle24Regular />}
              disabled
              data-testid="menu-item-help"
            >
              Help
            </MenuItem>
          </Tooltip>

          <MenuItem
            icon={<Info24Regular />}
            onClick={handleOpenAboutDialog}
            data-testid="menu-item-about"
          >
            About
          </MenuItem>

          <MenuDivider />

          {/* Sign Out */}
          <MenuItem
            icon={<SignOut24Regular />}
            onClick={handleLogout}
            data-testid="menu-item-sign-out"
          >
            Sign Out
          </MenuItem>
        </MenuList>
      </MenuPopover>
    </Menu>

    {/* About Dialog */}
    <Dialog
      open={isAboutDialogOpen}
      onOpenChange={(_, data) => setIsAboutDialogOpen(data.open)}
    >
      <DialogSurface style={{ maxWidth: '700px' }}>
        <DialogBody>
          <DialogTitle>About IRPilot</DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM }}>
              <Text>
                IRPilot is a secure out-of-band communications platform built on Microsoft 365.
              </Text>
              <div>
                <Text weight="semibold">Version: </Text>
                <Text>{__APP_VERSION__}</Text>
              </div>
              {user.role === 'global_admin' && (
                <div
                  data-testid="changelog-container"
                  style={{ maxHeight: '400px', overflowY: 'auto' }}
                >
                  <Markdown>{changelogContent}</Markdown>
                </div>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="primary" onClick={() => setIsAboutDialogOpen(false)}>
              Close
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  </>
  );
}
